import React, { useLayoutEffect, useRef, useState } from "react";
import NavigationTree2 from ".";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import { panelResized } from "../PanelResizer";
import type { SecondaryNavigatorTarget } from "./TreeContext";
import type BaseModel from "../../models/BaseModel";
import { itx } from "../../translationItx/itx";

const navigatorStyle: React.CSSProperties = { width: "100%", minWidth: 0, minHeight: 0, margin: 0 };
const dividerHeight = 8;

/** Shares the left sidebar between project navigation and connection targets. */
export default function NavigationSidebar() {
  useOnChangeWatcher(state.projectMetadata);
  const [secondaryTarget, setSecondaryTarget] = useState<SecondaryNavigatorTarget | null>(null);
  const showTargets = secondaryTarget !== null;
  const targets: Record<
    SecondaryNavigatorTarget,
    {
      root: BaseModel;
      instance: string;
      title: string;
      menu?:
        | "scenes" // no itx
        | "cabinets"; // no itx
    }
  > = {
    cabinets: {
      root: state.cabinets,
      instance: "Cabinets", // no itx
      title: itx("NavigationSidebar.schaltschrnke", "Schaltschränke"),
      menu: "cabinets", // no itx
    },
    scenes: {
      root: state.sceneGroup,
      instance: "Scenes", // no itx
      title: itx("NavigationSidebar.szenenMultiplexer", "Szenen & Multiplexer"),
      menu: "scenes", // no itx
    },
    addresses: {
      root: state.addresses,
      instance: "Addresses", // no itx
      title: itx("NavigationSidebar.gruppenadressen", "Gruppenadressen"),
    },
    topologies: { root: state.topologies, instance: "Topologies", title: "Topologie" },
    products: {
      root: state.templateGroups,
      instance: "Products", // no itx
      title: itx("NavigationSidebar.produkte", "Produkte"),
    },
  };
  const target = targets[secondaryTarget ?? "cabinets"]; // no itx
  const targetRoot = target.root;
  const targetInstance = target.instance;
  const excludedRoots = showTargets ? targetRoot : undefined;
  const container = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const dragOffset = useRef(0);
  const [split, setSplit] = useState(0.55);

  // Menu positions must be measured after the grid has adopted its new dimensions.
  useLayoutEffect(() => panelResized.raise(undefined, undefined), [split, showTargets, secondaryTarget]);

  /** Keeps both navigators reachable while adjusting their share of the available height. */
  function updateSplit(value: number): void {
    setSplit(Math.max(0.15, Math.min(0.85, value)));
  }

  /** Converts the pointer position into a split without a jump at the initial grab point. */
  function moveDivider(event: React.PointerEvent<HTMLDivElement>): void {
    if (!dragging.current || !container.current) return;
    const bounds = container.current.getBoundingClientRect();
    const availableHeight = bounds.height - dividerHeight;
    if (availableHeight > 0)
      updateSplit((event.clientY - bounds.top - dividerHeight / 2 - dragOffset.current) / availableHeight);
  }

  return (
    <div
      id="navigationSidebar"
      ref={container}
      style={{
        position: "relative",
        display: "grid",
        width: 300,
        minHeight: 0,
        flexShrink: 0,
        marginLeft: 5,
        zIndex: 30,
        gridTemplateColumns: "minmax(0, 1fr)",
        gridTemplateRows: showTargets
          ? `minmax(0, ${split}fr) ${dividerHeight}px minmax(0, ${1 - split}fr)`
          : "minmax(0, 1fr)",
      }}
    >
      <NavigationTree2
        excludedRootObject={excludedRoots}
        onOpenSecondaryNavigator={setSecondaryTarget}
        style={navigatorStyle}
      />
      {showTargets && (
        <>
          <div
            role="separator" // no itx
            aria-label="Höhen der Navigatoren anpassen"
            aria-orientation="horizontal" // no itx
            aria-controls={`navigationTreeOuter navigationTreeOuter${targetInstance}`}
            aria-valuemin={15}
            aria-valuemax={85}
            aria-valuenow={Math.round(split * 100)}
            tabIndex={0}
            style={{ cursor: "ns-resize", touchAction: "none", borderBlock: "1px solid var(--panel-background)" }}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              event.preventDefault();
              dragging.current = true;
              const bounds = event.currentTarget.getBoundingClientRect();
              dragOffset.current = event.clientY - (bounds.top + bounds.height / 2);
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={moveDivider}
            onPointerUp={(event) => {
              dragging.current = false;
              event.currentTarget.releasePointerCapture(event.pointerId);
            }}
            onLostPointerCapture={() => {
              dragging.current = false;
            }}
            onKeyDown={(event) => {
              if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
              event.preventDefault();
              updateSplit(split + (event.key === "ArrowUp" ? -0.05 : 0.05));
            }}
          />
          <NavigationTree2
            key={targetInstance}
            instanceId={targetInstance}
            rootObject={targetRoot}
            headerMenu={target.menu}
            headerTitle={target.title}
            onClose={() => setSecondaryTarget(null)}
            style={navigatorStyle}
          />
        </>
      )}
    </div>
  );
}
